'use client';

import { LogoSvg } from '@/components/svg/logo';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import HamburgerMenu from './hamburger-menu';
import AvatarContainer from './user-avatar';
import { ModeToggle } from './mode-toggle';
import { Links } from './links';
import { useTranslations } from '@/hooks/useTranslations';
import { withLocaleHref, type Locale } from '@/i18n/config';
import type { Dictionary } from '@/types';
import type { ExtendedUser } from '@/types/next-auth';

interface ProtectedHeaderProps {
  user: ExtendedUser | null;
  dictionary: Dictionary;
}

export function ProtectedHeader({ user, dictionary }: ProtectedHeaderProps) {
  const pathname = usePathname();
  const locale = (pathname.split('/')[1] || 'en') as Locale;
  const tAdmin = useTranslations('AdminUsers');
  const dashboardHref =
    user?.role === 'ACCOUNTANT'
      ? '/accountant'
      : user?.role === 'ADMIN'
        ? '/admin'
        : '/dashboard';

  return (
    <div className="flex justify-start gap-10 items-center px-5 py-5 border-b md:px-10 w-full">
      <Link
        href={withLocaleHref(dashboardHref, locale)}
        aria-label="Invoice Easy Dashboard"
      >
        <LogoSvg width="200" height="40" />
      </Link>

      {user && user.role !== 'ADMIN' && user.role !== 'ACCOUNTANT' && (
        <Links dictionary={dictionary} />
      )}

      {user?.role === 'ADMIN' && (
        <nav className="gap-2 hidden md:flex">
          <Link
            href={withLocaleHref('/admin', locale)}
            className="text-sm font-medium text-gray-900 hover:bg-accent px-3 py-2 rounded-sm dark:text-gray-400 dark:hover:text-white"
          >
            {tAdmin('adminTitle')}
          </Link>
          <Link
            href={withLocaleHref('/admin/emails', locale)}
            className="text-sm font-medium text-gray-900 hover:bg-accent px-3 py-2 rounded-sm dark:text-gray-400 dark:hover:text-white"
          >
            {tAdmin('emailLogs')}
          </Link>
          <Link
            href={withLocaleHref('/admin/tickets', locale)}
            className="text-sm font-medium text-gray-900 hover:bg-accent px-3 py-2 rounded-sm dark:text-gray-400 dark:hover:text-white"
          >
            {tAdmin('supportTickets')}
          </Link>
        </nav>
      )}

      <div className="flex items-center m-auto mr-0">
        {user && <HamburgerMenu user={user} />}
        {user && <AvatarContainer user={user} />}
        {!user && <ModeToggle />}
      </div>
    </div>
  );
}
